"use client"

import { useEffect, useRef, useState } from "react"

import { cn } from "@coval/ui/cn"

/** Longest title we store — matches the provider's own clamp in renameThread. */
const MAX_TITLE_LENGTH = 80

/**
 * Inline title editor, shared by the thread bar and the thread list so renaming
 * behaves identically wherever it is started: autofocused and pre-selected,
 * Enter commits, Escape reverts, blur commits. An empty or unchanged value
 * commits nothing, which keeps a stray click from blanking a title.
 */
export function ThreadRenameInput({
  title,
  onCommit,
  onCancel,
  className,
}: {
  title: string
  onCommit: (title: string) => void
  onCancel: () => void
  className?: string
}) {
  const [draft, setDraft] = useState(title)
  const inputRef = useRef<HTMLInputElement | null>(null)
  // Escape must not also fire the blur-commit that unmounting triggers.
  const cancelledRef = useRef(false)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const commit = () => {
    if (cancelledRef.current) {
      return
    }
    const trimmed = draft.trim()
    if (trimmed && trimmed !== title) {
      onCommit(trimmed)
    } else {
      onCancel()
    }
  }

  return (
    <input
      ref={inputRef}
      value={draft}
      maxLength={MAX_TITLE_LENGTH}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault()
          commit()
        }
        if (event.key === "Escape") {
          event.preventDefault()
          cancelledRef.current = true
          onCancel()
        }
      }}
      aria-label="Chat name"
      className={cn(
        "min-w-0 flex-1 rounded border border-coval-border bg-surface-default px-1.5 py-0.5",
        "text-coval-text outline-none focus:border-coval-info-text",
        className,
      )}
    />
  )
}
